/* eslint-disable react/prop-types */
import React, { useContext, useEffect, useMemo, useState } from "react";
import { useSortBy, useTable } from "react-table";
import TableLayout from "../../../../components/orderTable/table-layout";
import { OldOrderDetailsData, OrderDetailsData } from "../../../../contexts";
import ReOrderModal from "../../../../components/reorder-form/reorder-modal";
import { getOrderStatuses } from "../../../../utils/helpers";
import { PRESORTED, PRESORTED_ORDER_TYPE, STRIPS_ORDER_TYPE } from "../../../../utils/constants";
import { useSourcesData } from "../../../../hooks/fetch-hooks/get-sources";
import { Cell, Cell2, CellChannel, CellSource } from "../../../../components/orderTable/table-instance";
import { ORDERS_COLUMN } from "../../../../components/orderTable/Constants";
import { buildExtraOrderColumns } from "./columns";


/**
 * Minimum width for the three inline `<select>` cells.
 *
 * Without it they render ~44px wide — the value is invisible and ops have to open the
 * dropdown to read a status. The selects themselves are `w-[100%]` inside legacy's own
 * Cell helpers (src/components/orderTable/table-instance.jsx), which /all-orders also
 * renders, so the floor is applied here via a wrapper rather than by editing those.
 */
const SelectCell = ({ children }) => <div className="min-w-[180px]">{children}</div>;

/**
 * Left-to-right order for the columns ops read most, by react-table column id.
 *
 * Needed because the rendered set is assembled from three sources — legacy's
 * ORDERS_COLUMN, then buildExtraOrderColumns(), then the select/action cells — so a
 * column's position was an accident of which list it happened to live in. Contact, for
 * one, sat two places from the end despite belonging beside the name it identifies.
 *
 * Anything not listed keeps its existing relative order after these.
 */
const LEADING_COLUMN_IDS = [
    "id",
    "mapUserPatient.Patient.name",
    "status",
    "contact",
    "createdAt",
    "endDate",
    "total",
    "type",
];

/**
 * Stable-sort `columns` so LEADING_COLUMN_IDS come first, in that order.
 *
 * The `#` column is pinned in place rather than sorted: its accessor is `""`, so it has
 * no dependable react-table id to match on, and it is always the first column anyway.
 */
function orderColumns(columns) {
    const [first, ...rest] = columns;
    const pinned = first?.Header === "#" ? [first] : [];
    const sortable = pinned.length ? rest : columns;

    const rank = (column) => {
        const index = LEADING_COLUMN_IDS.indexOf(column.id || column.accessor);
        return index === -1 ? LEADING_COLUMN_IDS.length : index;
    };

    // map/sort on index keeps the sort stable for equal ranks, which is what preserves
    // the original relative order of everything not named above.
    const ordered = sortable
        .map((column, index) => ({ column, index }))
        .sort((a, b) => rank(a.column) - rank(b.column) || a.index - b.index)
        .map((entry) => entry.column);

    return [...pinned, ...ordered];
}

/**
 * `extraColumns` lets a second console reuse this whole table with a different set of
 * spliced-in columns. It defaults to the ops set, so /v2/ops/orders is unchanged.
 *
 * A prop rather than a forked copy of this file: the ops and pharmacy consoles already
 * learned that lesson -- the parallel v2 orders rewrites drifted (days-left was
 * Math.floor(x + 2) in one and Math.ceil(x) in the other, showing different numbers for
 * the same column). Everything that makes this table correct -- the per-row
 * PRESORTED/STRIPS status vocabulary, the Source-needs-Channel guard, the column
 * ordering, the react-table hidden-column plumbing -- is shared rather than copied.
 */
function LegacyOrdersTableInstance({
    tableData,
    changeRightBarOpen,
    setPageSort,
    pageSort,
    handleDeleteRow,
    refetchOrders,
    hiddenColumns,
    extraColumns = buildExtraOrderColumns,
}) {
    const orderStatuses = getOrderStatuses();
    const { channels, getSourcesForChannels } = useSourcesData();
    const { OrderType } = useContext(OrderDetailsData);
    const [modal, showModal] = useState(false);
    const reOrderModalClick = () => showModal((prev) => !prev);
    const [oldOrder, setOldOrder] = useState(null);

    const [columns, data] = useMemo(() => {
        const statusColumn = {
            Header: "Status",
            accessor: "status",
            Cell: (row) => {
                // Mirrors legacy's own ALL_ORDER_TYPE branch
                // (table-instance.jsx:337): PRESORTED vs STRIPS statuses, chosen
                // per row from the row's own type — same rule Presorted/Strips
                // sub-tabs use, since a mixed "All Orders" view has both.
                const types = row?.cell?.row?.original?.type?.includes(PRESORTED)
                    ? orderStatuses[PRESORTED_ORDER_TYPE]
                    : orderStatuses[STRIPS_ORDER_TYPE];
                return <SelectCell>{Cell2(row, "status", types, refetchOrders)}</SelectCell>;
            },
        };

        // Assembled in source order, then positioned by orderColumns — which is what
        // lifts Status from 27th to 4th, immediately after Name. "Does this order need
        // me?" is the question the queue exists to answer, and it used to sit ~1000px of
        // horizontal scroll away from the ID and Name that say which order it is, so
        // acting on a row meant losing sight of whose row it was. TableLayout dispatches
        // on `Header` string rather than column index, so reordering changes nothing
        // about how any cell renders.
        const columns = orderColumns([
            ...ORDERS_COLUMN,
            ...extraColumns(),
            statusColumn,
            {
                Header: "Channel",
                accessor: "channel",
                Cell: (row) => <SelectCell>{CellChannel(row, "channel", channels, refetchOrders)}</SelectCell>,
            },
            {
                Header: "Source",
                accessor: "source",
                Cell: (row) => {
                    const sources = getSourcesForChannels(row?.row?.original?.channel);
                    const current = row?.row?.original?.source;
                    if (!sources?.length) {
                        return current ? (
                            <span>{current}</span>
                        ) : (
                            <span className="text-gray-400 text-sm">Set Channel first</span>
                        );
                    }
                    return <SelectCell>{CellSource(row, "source", sources, refetchOrders)}</SelectCell>;
                },
            },
            {
                Header: "Action",
                accessor: "action",
                Cell: (row) => Cell(row, reOrderModalClick, handleDeleteRow, OrderType),
            },
        ]);
        return [columns, tableData];
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [tableData, extraColumns]);

    const tableInstance = useTable(
        {
            columns,
            data,
            initialState: { sortBy: pageSort, hiddenColumns },
            manualSortBy: true,
            autoResetSortBy: false,
            autoResetExpanded: false,
            autoResetPage: false,
            // Without this, react-table resets hidden columns every time `data`
            // changes — i.e. on every refetch, page change and status write — so the
            // user's column choice would silently snap back mid-session.
            autoResetHiddenColumns: false,
        },
        useSortBy
    );

    // `initialState` is read once, so it cannot carry later changes from the picker.
    // Pushing the prop in here is react-table's supported way to drive visibility from
    // outside. Guarded on a value comparison because setHiddenColumns triggers a
    // re-render, and an unguarded call would loop.
    const { setHiddenColumns, state: tableState } = tableInstance;
    useEffect(() => {
        const current = tableState.hiddenColumns || [];
        const next = hiddenColumns || [];
        const same = current.length === next.length && next.every((id) => current.includes(id));
        if (!same) setHiddenColumns(next);
    }, [hiddenColumns, tableState.hiddenColumns, setHiddenColumns]);

    return (
        <OldOrderDetailsData.Provider value={{ oldOrder, setOldOrder }}>
            {oldOrder && oldOrder.addressId !== "0" && (
                <ReOrderModal oldOrderDetails={oldOrder} value={modal} action={reOrderModalClick} id={oldOrder.id} />
            )}
            <TableLayout
                {...tableInstance}
                changeRightBarOpen={changeRightBarOpen}
                setPageSort={setPageSort}
                refetchOrders={refetchOrders}
            />
        </OldOrderDetailsData.Provider>
    );
}

export default LegacyOrdersTableInstance;
